/**
 * AdminImpersonationPage — Jul-2026 (User-Wunsch).
 *
 * Ein zentraler Ort für den Admin, um:
 *  1) JEDEN Nutzer (alle Rollen: Händler, Vermittler, Mitarbeiter, …) zu suchen
 *     und per Klick in dessen Konto zu wechseln,
 *  2) den Verlauf der letzten Konto-Wechsel (Audit) nachzuvollziehen.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, LogIn, Loader2, ShieldAlert, History } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const ROLES = [
    { value: "all", label: "Alle Rollen" },
    { value: "b2b", label: "Händler" },
    { value: "vermittler", label: "Vermittler / Privat" },
    { value: "zweiradverkaeufer", label: "Mitarbeiter" },
    { value: "b2c", label: "Privatkunden" },
];

const ROLE_LABEL = {
    b2b: "Händler", vermittler: "Vermittler", zweiradverkaeufer: "Mitarbeiter",
    b2c: "Privatkunde", admin: "Admin",
};

const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
};

export default function AdminImpersonationPage() {
    const { user, loading, impersonate } = useAuth();
    const navigate = useNavigate();
    const [q, setQ] = useState(() => {
        if (typeof window === "undefined") return "";
        return new URLSearchParams(window.location.search).get("q") || "";
    });
    const [role, setRole] = useState("all");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [log, setLog] = useState([]);
    const [busyId, setBusyId] = useState(null);
    // Jul-2026 (User-Wunsch) — Verlauf-Filter + Monatsabrechnung der Arbeitszeit
    const [logQ, setLogQ] = useState("");
    const [logFrom, setLogFrom] = useState("");
    const [logTo, setLogTo] = useState("");
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [billing, setBilling] = useState(null);

    const search = useCallback(async () => {
        setSearching(true);
        try {
            const { data } = await api.get("/admin/users/search", { params: { q, role, limit: 40 } });
            setResults(data.items || []);
        } catch { toast.error("Suche fehlgeschlagen"); }
        finally { setSearching(false); }
    }, [q, role]);

    // Universalsuche-Deeplink (?q=…) — bei vorbefülltem Suchbegriff direkt suchen.
    useEffect(() => {
        if (q) search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLog = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/impersonation/log", {
                params: { limit: 100, q: logQ, date_from: logFrom, date_to: logTo },
            });
            setLog(data.items || []);
        } catch { /* still show page */ }
    }, [logQ, logFrom, logTo]);

    const loadBilling = useCallback(async (m) => {
        try {
            const { data } = await api.get("/admin/impersonation/billing", { params: { month: m } });
            setBilling(data);
        } catch { setBilling(null); }
    }, []);

    useEffect(() => { if (user?.role === "admin") loadBilling(month); }, [user, month, loadBilling]);
    useEffect(() => { if (user?.role === "admin") loadLog(); }, [user, loadLog]);

    useEffect(() => { if (user?.role === "admin") { search(); loadLog(); } }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const doImpersonate = async (id) => {
        setBusyId(id);
        try {
            await impersonate(id);
            toast.success("In Konto gewechselt");
            navigate("/dashboard");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Wechsel fehlgeschlagen");
        } finally { setBusyId(null); }
    };

    if (loading) return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
    if (!user || user.role !== "admin") {
        return <div className="max-w-2xl mx-auto py-16 text-center text-gray-600" data-testid="impersonation-forbidden">Nur für Admins.</div>;
    }

    return (
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-8" data-testid="admin-impersonation-page">
            <div>
                <h1 className="font-display font-black text-3xl text-[#111827] flex items-center gap-2">
                    <ShieldAlert className="w-7 h-7 text-[#B45309]" /> Konto-Wechsel
                </h1>
                <p className="text-sm text-gray-500 mt-1">In jedes Konto wechseln (außer Admin). Jeder Wechsel wird protokolliert.</p>
            </div>

            {/* Suche */}
            <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-3">
                <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex flex-wrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Name, E-Mail, Firma oder Ort suchen…"
                            className="pl-8 rounded-sm"
                            data-testid="impersonation-search-input"
                        />
                    </div>
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="border border-gray-300 rounded-sm text-sm px-2 py-2 bg-white"
                        data-testid="impersonation-role-filter"
                    >
                        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <Button type="submit" className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="impersonation-search-btn">
                        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suchen"}
                    </Button>
                </form>

                <div className="divide-y divide-gray-100" data-testid="impersonation-results">
                    {results.length === 0 ? (
                        <div className="py-6 text-center text-sm text-gray-500" data-testid="impersonation-results-empty">Keine Treffer.</div>
                    ) : results.map((u) => (
                        <div key={u.id} className="flex items-center gap-3 py-2.5" data-testid={`impersonation-result-${u.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="font-bold text-sm text-[#111827] truncate">
                                    {u.company_name || u.name || u.email}
                                    {u.is_active === false && <span className="ml-2 text-[10px] text-red-600 font-bold">(deaktiviert)</span>}
                                </div>
                                <div className="text-[11px] text-gray-500 font-mono-num truncate">{u.email}{u.city ? ` · ${u.city}` : ""}</div>
                            </div>
                            <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-[10px] font-bold shrink-0">{ROLE_LABEL[u.role] || u.role}</Badge>
                            <Button
                                size="sm"
                                disabled={busyId === u.id || u.is_active === false}
                                onClick={() => doImpersonate(u.id)}
                                className="rounded-sm text-xs font-bold uppercase tracking-wider bg-[#B45309] hover:bg-[#92400E] text-white shrink-0"
                                data-testid={`impersonate-${u.id}`}
                            >
                                {busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5 mr-1" />}
                                In Konto wechseln
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Verlauf */}
            <div>
                <h2 className="font-display font-bold text-lg text-[#111827] flex items-center gap-2 mb-3">
                    <History className="w-5 h-5 text-gray-500" /> Verlauf der Konto-Wechsel
                </h2>
                <form onSubmit={(e) => { e.preventDefault(); loadLog(); }} className="flex flex-wrap gap-2 items-center mb-3" data-testid="impersonation-log-filters">
                    <Input value={logQ} onChange={(e) => setLogQ(e.target.value)} placeholder="Ziel-Konto (E-Mail) filtern…" className="rounded-sm w-56" data-testid="impersonation-log-q" />
                    <input type="date" value={logFrom} onChange={(e) => setLogFrom(e.target.value)} className="border border-gray-300 rounded-sm text-sm px-2 py-2" data-testid="impersonation-log-from" />
                    <span className="text-xs text-gray-400">bis</span>
                    <input type="date" value={logTo} onChange={(e) => setLogTo(e.target.value)} className="border border-gray-300 rounded-sm text-sm px-2 py-2" data-testid="impersonation-log-to" />
                    <Button type="submit" size="sm" className="rounded-sm bg-[#111827] text-white font-bold" data-testid="impersonation-log-apply">Filtern</Button>
                    {(logQ || logFrom || logTo) && (
                        <button type="button" onClick={() => { setLogQ(""); setLogFrom(""); setLogTo(""); }} className="text-xs underline text-gray-500" data-testid="impersonation-log-reset">Zurücksetzen</button>
                    )}
                </form>
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm" data-testid="impersonation-log-table">
                        <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-2.5">Zeitpunkt</th>
                                <th className="text-left px-4 py-2.5">Aktion</th>
                                <th className="text-left px-4 py-2.5">Ziel-Konto</th>
                                <th className="text-left px-4 py-2.5">Admin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {log.length === 0 ? (
                                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="impersonation-log-empty">Noch keine Konto-Wechsel protokolliert.</td></tr>
                            ) : log.map((r, i) => (
                                <tr key={i} className="border-b border-gray-100" data-testid="impersonation-log-row">
                                    <td className="px-4 py-2.5 font-mono-num text-[12px] text-gray-600">{fmt(r.at)}</td>
                                    <td className="px-4 py-2.5">
                                        {r.event === "admin_impersonation_started"
                                            ? <span className="text-[11px] font-bold text-[#B45309]">Gewechselt</span>
                                            : <span className="text-[11px] font-bold text-gray-500">Zurückgekehrt</span>}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <span className="font-mono-num text-[12px]">{r.target_email || "—"}</span>
                                        {r.target_role && <span className="ml-2 text-[10px] text-gray-500">({ROLE_LABEL[r.target_role] || r.target_role})</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-[12px] text-gray-600">{r.admin || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Monatsabrechnung Arbeitszeit */}
            <div>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h2 className="font-display font-bold text-lg text-[#111827]">Monatsabrechnung — Arbeitszeit je Konto</h2>
                    <div className="flex items-center gap-2">
                        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-gray-300 rounded-sm text-sm px-2 py-2" data-testid="impersonation-billing-month" />
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm text-xs font-bold"
                            data-testid="impersonation-billing-csv"
                            onClick={() => {
                                const rows = [["Konto", "E-Mail", "Rolle", "Sitzungen", "Minuten gesamt", "Stunden"]];
                                (billing?.groups || []).forEach((g) => rows.push([
                                    g.target_label, g.target_email, g.target_role, g.sessions,
                                    g.total_minutes, (g.total_minutes / 60).toFixed(2).replace(".", ","),
                                ]));
                                const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
                                const a = document.createElement("a");
                                a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
                                a.download = `Arbeitszeit_Konten_${billing?.month || month}.csv`;
                                a.click();
                            }}
                        >
                            CSV-Export
                        </Button>
                    </div>
                </div>
                <p className="text-xs text-gray-500 -mt-2 mb-3">Gemessene Zeit pro Konto-Wechsel-Sitzung (Wechsel → Rückkehr). Grundlage für die Abrechnung am Monatsende.</p>
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm" data-testid="impersonation-billing-table">
                        <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-2.5">Konto</th>
                                <th className="text-left px-4 py-2.5">Rolle</th>
                                <th className="text-right px-4 py-2.5">Sitzungen</th>
                                <th className="text-right px-4 py-2.5">Zeit gesamt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!(billing?.groups || []).length ? (
                                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="impersonation-billing-empty">Keine Sitzungen in {month}.</td></tr>
                            ) : billing.groups.map((g) => (
                                <tr key={g.target_id} className="border-b border-gray-100" data-testid="impersonation-billing-row">
                                    <td className="px-4 py-2.5">
                                        <div className="font-bold text-[13px]">{g.target_label}</div>
                                        <div className="text-[11px] text-gray-500 font-mono-num">{g.target_email}</div>
                                    </td>
                                    <td className="px-4 py-2.5 text-[12px] text-gray-600">{ROLE_LABEL[g.target_role] || g.target_role}</td>
                                    <td className="px-4 py-2.5 text-right font-mono-num">{g.sessions}{g.open_sessions ? <span className="text-[10px] text-amber-600"> (+{g.open_sessions} offen)</span> : null}</td>
                                    <td className="px-4 py-2.5 text-right font-mono-num font-bold">
                                        {Math.floor(g.total_minutes / 60)}:{String(g.total_minutes % 60).padStart(2, "0")} h
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
